/**
 * Screen IDs used to navigate to native app screens from the webview.
 * These must be coordinated with the Kotlin (Android) and Swift (iOS) navigation routers.
 */
export const WebviewScreenId = {
  // Dashboard widgets
  MATCH_DETAIL: 'MATCH_DETAIL',
  PLAYER_PROFILE: 'PLAYER_PROFILE',
  WPR_RANKINGS: 'WPR_RANKINGS',
  NEWS_ARTICLE: 'NEWS_ARTICLE',
  NEWS_LIST: 'NEWS_LIST',

  // Dashboard sidebar pages (the current player's own pages — no params)
  DASHBOARD: 'DASHBOARD',
  NOTIFICATIONS: 'NOTIFICATIONS',
  EDIT_PROFILE: 'EDIT_PROFILE',
  PROFILE_IMAGES: 'PROFILE_IMAGES',
  ASSOCIATION_MEMBERSHIPS: 'ASSOCIATION_MEMBERSHIPS',
  SPONSORS: 'SPONSORS',
  UPDATE_PASSWORD: 'UPDATE_PASSWORD',
  CONNECT_DUPR: 'CONNECT_DUPR',
  BIOGRAPHY: 'BIOGRAPHY',
  FRIENDS: 'FRIENDS',
  FOLLOWING: 'FOLLOWING',
  PRO_NETWORK: 'PRO_NETWORK',
  PLAYER_SEARCH: 'PLAYER_SEARCH',
  ACHIEVEMENTS: 'ACHIEVEMENTS',
  MY_GEAR: 'MY_GEAR',
  RATING_HISTORY: 'RATING_HISTORY',
  UPCOMING_PLAY_SCHEDULE: 'UPCOMING_PLAY_SCHEDULE',
  PLAY_OPPORTUNITIES: 'PLAY_OPPORTUNITIES',
  LOGIN_HISTORY: 'LOGIN_HISTORY',
  PASSWORD_HISTORY: 'PASSWORD_HISTORY',
  PAYMENT_METHODS: 'PAYMENT_METHODS',
  APP_ACCESS: 'APP_ACCESS',
  SUBSCRIPTIONS: 'SUBSCRIPTIONS',
  ACCOUNTS_I_MANAGE: 'ACCOUNTS_I_MANAGE',
  ACCOUNTS_MANAGING_ME: 'ACCOUNTS_MANAGING_ME',
  INFORMATION_PROTECTION: 'INFORMATION_PROTECTION',

  // Pickleball+ (non-prod until launch)
  CLUB_1965: 'CLUB_1965',
  REWARD_POINTS_OVERVIEW: 'REWARD_POINTS_OVERVIEW',
  REWARD_POINTS_ACTIVITY: 'REWARD_POINTS_ACTIVITY',
  PICKLEBALL_PLUS_AT_A_GLANCE: 'PICKLEBALL_PLUS_AT_A_GLANCE',
  PICKLEBALL_PLUS_MY_BENEFITS: 'PICKLEBALL_PLUS_MY_BENEFITS',
  PICKLEBALL_PLUS_MEMBER_PERKS: 'PICKLEBALL_PLUS_MEMBER_PERKS',
  ADD_ONS: 'ADD_ONS',
} as const;

export type WebviewScreenId = (typeof WebviewScreenId)[keyof typeof WebviewScreenId];

/**
 * Params required by the screens that take them. Every other screen takes no params.
 * The native router must handle these param keys exactly as specified.
 */
type WebviewScreenParamsMap = {
  [WebviewScreenId.MATCH_DETAIL]: { matchId: string };
  [WebviewScreenId.PLAYER_PROFILE]: { slug: string };
  [WebviewScreenId.NEWS_ARTICLE]: { slug: string };
};

export type WebviewScreenParams = {
  [K in WebviewScreenId]: K extends keyof WebviewScreenParamsMap
    ? WebviewScreenParamsMap[K]
    : never;
};

/** Screen IDs that take no params — safe to navigate to with just the id. */
export type ParamlessWebviewScreenId = {
  [K in WebviewScreenId]: [WebviewScreenParams[K]] extends [never] ? K : never;
}[WebviewScreenId];

// Interim fallback until the app injects `window.__SUPPORTED_NATIVE_SCREENS__`; populate as screens ship.
export const FALLBACK_SUPPORTED_NATIVE_SCREENS: ParamlessWebviewScreenId[] = [];
